import { ReactNode, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onBoardingSurveyStatus } from "../Controllers/OnboardingApi";
import { useSupabaseAuth } from "../Context/AuthContext/AuthContext";

interface BoardingSurveyWrapperProps {
  children: ReactNode;
}

const BoardingSurveyWrapper = ({ children }: BoardingSurveyWrapperProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [canProceed, setCanProceed] = useState(false);
  const hasCheckedOnboarding = useRef(false);
  const location = useLocation();
  const checkOnboarding = location.state?.checkOnboarding || false;
  const { assignPaywallGroup, useAuthStore, getUserProfile } =
    useSupabaseAuth();
  // Use the store subscription to get real-time updates
  const user = useAuthStore((state: any) => state.user);
  const profile = useAuthStore((state: any) => state.profile);
  // Get user_id from the session user data
  const userId = user?.user?.id;
  useEffect(() => {
    // Only check onboarding status if checkOnboarding is true
    if (!checkOnboarding) {
      setCanProceed(true);
      return;
    }

    const checkOnboardingStatus = async () => {
      // Prevent duplicate API calls
      if (hasCheckedOnboarding.current) {
        return;
      }

      hasCheckedOnboarding.current = true;

      try {
        setIsLoading(true);

        // If we have userId but no profile yet, fetch the profile first
        let currentProfile = profile;
        if (userId && !profile) {
          console.log("User found but no profile, fetching profile...");
          try {
            const fetchedProfile = await getUserProfile(userId);
            currentProfile = fetchedProfile;
          } catch (error) {
            console.error("Error fetching user profile:", error);
            // Continue with the flow even if profile fetch fails
          }
        }

        // Only call assignPaywallGroup if hardPaywall is not true and we have a valid userId
        const currentHardPaywall = currentProfile?.hard_paywall;
        if (userId && !currentHardPaywall) {
          console.log("Calling assignPaywallGroup with userId:", userId);
          try {
            const result = await assignPaywallGroup(userId);
            console.log("assignPaywallGroup result:", result);
          } catch (error) {
            console.error("Error in assignPaywallGroup:", error);
          }
        } else {
          console.log("Skipping assignPaywallGroup:", { hardPaywall: currentHardPaywall, userId });
        }

        const response = await onBoardingSurveyStatus();
        if (response.data?.exists === true) {
          setCanProceed(true);
        } else {
          if (response.data?.exists === false) {
            navigate("/on-boarding");
          } else {
            setCanProceed(true);
          }
          return;
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
        // On API error, allow user to proceed to prevent getting stuck
        setCanProceed(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [checkOnboarding, navigate]);

  // If checkOnboarding is false, directly render children
  if (!checkOnboarding) {
    return <>{children}</>;
  }

  // Show loading state while checking onboarding status
  if (isLoading) {
    return (
      <div className="h-screen w-full flex justify-center items-center bg-[#000513]">
         <l-waveform size="35" stroke="3.5" speed="1" color="white"></l-waveform>
      </div>
    );
  }

  // Only render children if user can proceed (has completed onboarding)
  return canProceed ? <>{children}</> : null;
};

export default BoardingSurveyWrapper;
